// Admin Warehouse view — tree (racks → shelves → bins) + warehouse meta
import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Boxes, Package, MapPin } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const AdminWarehouse = () => {
    const [warehouses, setWarehouses] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [tree, setTree] = useState(null);
    const [openRacks, setOpenRacks] = useState({});
    const [openShelves, setOpenShelves] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/warehouses").then((r) => {
            setWarehouses(r.data.items || []);
            if (r.data.items?.length) setActiveId(r.data.items[0].id);
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!activeId) return;
        api.get(`/admin/inventory/tree?warehouse_id=${activeId}`).then((r) => setTree(r.data));
    }, [activeId]);

    const active = warehouses.find((w) => w.id === activeId);

    return (
        <AdminAuthGuard>
            <AdminLayout title="Warehouse" subtitle="Racks, shelves, bins · FIFO inventory">
                {loading ? <div className="h-48 skeleton rounded-2xl" /> : (
                    <div className="space-y-5" data-testid="admin-warehouse">
                        <div className="flex gap-2">
                            {warehouses.map((w) => (
                                <button
                                    key={w.id}
                                    onClick={() => setActiveId(w.id)}
                                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${activeId === w.id ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`}
                                    data-testid={`admin-warehouse-tab-${w.id}`}
                                >
                                    {w.name}
                                </button>
                            ))}
                        </div>

                        {active && (
                            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-warehouse-tree">
                                    <p className="label-uppercase">Location tree</p>
                                    <h2 className="text-lg font-semibold tracking-tight">{active.name}</h2>
                                    <ul className="mt-4 space-y-1.5 text-sm">
                                        {(tree?.racks || []).map((rack) => (
                                            <li key={rack.id}>
                                                <button onClick={() => setOpenRacks((o) => ({ ...o, [rack.id]: !o[rack.id] }))} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-zinc-50" data-testid={`admin-warehouse-rack-${rack.code}`}>
                                                    {openRacks[rack.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                                    <Boxes className="h-3.5 w-3.5 text-brand-700" />
                                                    {rack.code} · {rack.label}
                                                    <span className="ml-auto text-[10px] text-zinc-400">{rack.shelves.length} shelves</span>
                                                </button>
                                                {openRacks[rack.id] && (
                                                    <ul className="ml-5 space-y-1 border-l border-zinc-100 pl-2">
                                                        {rack.shelves.map((shelf) => (
                                                            <li key={shelf.id}>
                                                                <button onClick={() => setOpenShelves((o) => ({ ...o, [shelf.id]: !o[shelf.id] }))} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs font-medium hover:bg-zinc-50">
                                                                    {openShelves[shelf.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                                                    {shelf.code}
                                                                    <span className="ml-auto text-[10px] text-zinc-400">{shelf.bins.length} bins</span>
                                                                </button>
                                                                {openShelves[shelf.id] && (
                                                                    <ul className="ml-5 grid grid-cols-2 gap-1.5 pt-1">
                                                                        {shelf.bins.map((bin) => (
                                                                            <li key={bin.id} className="rounded-md border border-zinc-100 px-2 py-1.5 text-[11px]" data-testid={`admin-warehouse-bin-${bin.code}`}>
                                                                                <p className="font-medium text-zinc-900">{bin.code}</p>
                                                                                <p className="text-zinc-500">{bin.batch_count} batch · qty {bin.qty_total}</p>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <aside className="space-y-5">
                                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                                        <p className="label-uppercase">Stats</p>
                                        <ul className="mt-3 space-y-2 text-sm">
                                            <li className="flex justify-between"><span className="text-zinc-500">Bins</span><span className="font-semibold">{active.bin_count}</span></li>
                                            <li className="flex justify-between"><span className="text-zinc-500">Inventory batches</span><span className="font-semibold">{active.batch_count}</span></li>
                                            <li className="flex justify-between"><span className="text-zinc-500">Racks</span><span className="font-semibold">{tree?.racks?.length || 0}</span></li>
                                        </ul>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                                        <p className="label-uppercase">Address</p>
                                        <p className="mt-2 text-sm flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-zinc-400" /><span className="text-zinc-700">{active.address?.line1}, {active.address?.line2 || ""}<br />{active.address?.city}, {active.address?.state} — {active.address?.pincode}</span></p>
                                        <p className="mt-2 text-[10px] uppercase tracking-wider text-slate-400">GST State: {active.gst_state_code}</p>
                                    </div>
                                </aside>
                            </div>
                        )}
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminWarehouse;
